import { useEffect, useState } from "react";
import { CalendarDays, Car, Mail, Phone, UserPlus, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, fmtDateTime, money, telLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHead } from "@/components/Bits";
import { Card, CardContent } from "@/components/ui/card";
import { P } from "@/components/studio/portalKit";

const Line = ({ icon: Icon, children, testid }) => (
  <p data-testid={testid} className="flex items-center gap-2 text-sm text-slate-700"><Icon className="h-4 w-4 shrink-0 text-brand-deep/60" />{children}</p>
);

const SaleCard = ({ j, onAdd }) => {
  const v = j.vehicle || {};
  return (
    <Card data-testid={`central-sale-${j.id}`} className="transition-shadow hover:shadow-md">
      <CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-lg font-semibold text-brand-ink">{j.customer_name}</p>
            <p className="text-xs text-slate-500">Sale from Central Sales Team{j.cs_person_name && ` · ${j.cs_person_name}`} · sent {fmtDateTime(j.sent_at)}</p>
          </div>
          <span data-testid={`central-sale-price-${j.id}`} className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-emerald-800">{money(j.price)}</span>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Line icon={Phone} testid={`central-sale-phone-${j.id}`}><a href={telLink(j.customer_phone)} className="hover:underline">{j.customer_phone || "—"}</a>{j.customer_location && ` · ${j.customer_location}`}</Line>
          {j.customer_email && <Line icon={Mail}>{j.customer_email}</Line>}
          <Line icon={Car} testid={`central-sale-vehicle-${j.id}`}>{[v.make, v.model, v.colour, v.plate].filter(Boolean).join(" · ") || "—"}</Line>
          <Line icon={CalendarDays} testid={`central-sale-booked-${j.id}`}>Booked: {j.booked_date ? fmtDate(j.booked_date) : "Not booked yet"}</Line>
        </div>
        <Line icon={Wrench} testid={`central-sale-services-${j.id}`}>{j.services.join(", ") || "Service not set"}</Line>
        {j.notes && <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{j.notes}</p>}
        {j.in_customers ? (
          <p data-testid={`central-sale-added-${j.id}`} className="text-sm font-medium text-emerald-700">✓ In your customer list</p>
        ) : (
          <Button size="sm" data-testid={`central-sale-add-${j.id}`} onClick={() => onAdd(j)} className="h-10 bg-brand-deep hover:bg-brand-deep/90">
            <UserPlus className="mr-1.5 h-4 w-4" />Add to customer list
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default function StudioCentralSales() {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`${P}/central-sales`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const add = async (j) => {
    try { await api.post(`${P}/central-sales/${j.id}/add-to-customers`); toast.success("Added to your customer list"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6">
      <PageHead title="Central Sales Jobs" subtitle="Customers booked for your studio by the VeloGuardX Central Sales Team." />
      {rows?.length === 0 && <p className="text-sm text-slate-500">No jobs sent to your studio yet.</p>}
      <div className="grid gap-4 xl:grid-cols-2">{(rows || []).map((j) => <SaleCard key={j.id} j={j} onAdd={add} />)}</div>
    </div>
  );
}
